import { Plus, Power, UserMinus, UserPlus } from 'lucide-react';
import ENTITY_ICONS from '../components/common/entityIcons';
import React, { useEffect, useState } from 'react';
import FeatureOffState, { isFeatureOffError, useFeatureGate } from '../components/common/FeatureOffState';
import { useNotice } from '../components/common/Notices';
import { useConfirm } from '../components/common/ConfirmDialog';
import { Button } from '../components/ui/Button';
import DataTable, { RowAction, RowActions } from '../components/common/DataTable';
import StatusBadge from '../components/common/StatusBadge';
import { STATUS_OPTIONS, formatDate } from '../components/common/filters';
import ErrorState from '../components/common/ErrorState';
import Modal from '../components/common/Modal';
import PageHeader from '../components/common/PageHeader';
import SkeletonList from '../components/common/SkeletonList';
import { useApps } from '../contexts/AppsContext';
import useServerTable from '../hooks/useServerTable';
import useAppUsers, { ALL_APPS } from '../hooks/useAppUsers';
import groupsService from '../services/groups';
import { Field } from '../components/ui/Field';
import { TextInput, Select, Textarea } from '../components/ui/Input';

const GROUP_TYPE_OPTIONS = [
    { value: 'team', label: 'Team' },
    { value: 'department', label: 'Department' },
    { value: 'region', label: 'Region' },
    { value: 'care_team', label: 'Care team' },
    { value: 'pod', label: 'Pod' },
    { value: 'custom', label: 'Custom' },
];

const MEMBER_ROLE_OPTIONS = [
    { value: 'member', label: 'Member' },
    { value: 'lead', label: 'Lead' },
    { value: 'supervisor', label: 'Supervisor' },
    { value: 'observer', label: 'Observer' },
];

const errorText = (err, fallback) => err?.response?.data?.message || fallback;

const GroupsPage = () => {
    const { loading: loadingApps, selectedAppId: appId, selectedApp } = useApps();
    const notice = useNotice();
    const confirm = useConfirm();
    const gate = useFeatureGate(appId);
    const [error, setError] = useState('');

    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showAddMemberModal, setShowAddMemberModal] = useState(false);
    // Any active user can join a group; the list is refetched each time the modal opens.
    const usersQuery = useAppUsers(ALL_APPS, { enabled: showAddMemberModal });
    const allUsers = usersQuery.items;
    const [selectedGroup, setSelectedGroup] = useState(null);
    // Group whose members modal is open (replaces the old expand-in-card member list).
    const [membersGroup, setMembersGroup] = useState(null);
    const [parentOptions, setParentOptions] = useState([]);

    const [formData, setFormData] = useState({
        group_name: '',
        group_type: 'team',
        description: '',
        parent_group_id: '',
    });
    const [memberFormData, setMemberFormData] = useState({
        user_id: '',
        role_in_group: 'member',
    });

    // URL keys: status, type, members_min/members_max, created_from/created_to; members modal: role.
    const columns = [
        {
            key: 'group_name',
            header: 'Group',
            sortKey: 'name',
            locked: true,
            render: (g) => (
                <div className="min-w-0 max-w-sm">
                    <div className="font-medium text-slate-900">{g.group_name}</div>
                    {g.description && (
                        <div className="text-xs text-slate-500 truncate" title={g.description}>
                            {g.description}
                        </div>
                    )}
                </div>
            ),
        },
        {
            key: 'group_type',
            header: 'Type',
            sortKey: 'type',
            filter: { key: 'type', type: 'multiselect', options: GROUP_TYPE_OPTIONS, defaultExpanded: true },
        },
        {
            key: 'parent_group_name',
            header: 'Parent',
            sortKey: 'parent',
            render: (g) => g.parent_group_name || <span className="text-slate-400">—</span>,
        },
        {
            key: 'member_count',
            header: 'Members',
            sortKey: 'members',
            filter: { key: 'members', type: 'number' },
            render: (g) =>
                g.is_active ? (
                    <button
                        type="button"
                        onClick={() => openMembers(g)}
                        className="text-primary-700 hover:underline tabular-nums"
                    >
                        {g.member_count} member{g.member_count !== 1 ? 's' : ''}
                    </button>
                ) : (
                    <span className="tabular-nums">
                        {g.member_count} member{g.member_count !== 1 ? 's' : ''}
                    </span>
                ),
        },
        {
            key: 'status',
            header: 'Status',
            sortKey: 'status',
            render: (g) => <StatusBadge active={Boolean(g.is_active)} />,
            filter: { type: 'multiselect', options: STATUS_OPTIONS, defaultExpanded: true },
        },
        {
            key: 'created',
            header: 'Created',
            sortKey: 'created',
            defaultVisible: false,
            className: 'whitespace-nowrap text-slate-500',
            render: (g) => (g.created_on ? formatDate(g.created_on) : '—'),
            filter: { type: 'date' },
        },
        {
            key: 'actions',
            header: <span className="sr-only">Actions</span>,
            label: 'Row actions',
            locked: true,
            align: 'right',
            render: (g) => (
                <RowActions>
                    {/* The members endpoint only serves active groups. */}
                    <RowAction
                        icon={ENTITY_ICONS.users}
                        label="View members"
                        tone="info"
                        disabled={!g.is_active}
                        onClick={() => openMembers(g)}
                    />
                    <RowAction icon={UserPlus} label="Add member" onClick={() => openAddMember(g)} />
                    <RowAction
                        icon={Power}
                        label={g.is_active ? 'Deactivate group' : 'Activate group'}
                        tone={g.is_active ? 'warning' : 'success'}
                        onClick={() => handleToggleStatus(g)}
                    />
                </RowActions>
            ),
        },
    ];

    const memberColumns = [
        {
            key: 'name',
            header: 'Name',
            sortKey: 'name',
            locked: true,
            render: (m) => (
                <div className="min-w-0">
                    <div className="font-medium text-slate-900">
                        {[m.first_name, m.last_name].filter(Boolean).join(' ') || m.username}
                    </div>
                    <div className="text-xs text-slate-500">{m.username}</div>
                </div>
            ),
        },
        { key: 'email_id', header: 'Email', sortKey: 'email', className: 'text-slate-500' },
        {
            key: 'role_in_group',
            header: 'Role',
            sortKey: 'role',
            filter: { key: 'role', type: 'multiselect', options: MEMBER_ROLE_OPTIONS, defaultExpanded: true },
            render: (m) => <span className="px-2 py-0.5 rounded bg-slate-100 text-xs">{m.role_in_group}</span>,
        },
        {
            key: 'effective_from',
            header: 'Since',
            sortKey: 'since',
            className: 'text-slate-500 tabular-nums',
            render: (m) => m.effective_from?.slice(0, 10) || '—',
        },
        {
            key: 'actions',
            header: <span className="sr-only">Actions</span>,
            label: 'Row actions',
            locked: true,
            align: 'right',
            render: (m) => (
                <RowActions>
                    <RowAction
                        icon={UserMinus}
                        label="Remove member"
                        tone="danger"
                        onClick={() => handleRemoveMember(m)}
                    />
                </RowActions>
            ),
        },
    ];

    // Disabled groups are listed too (so they can be re-enabled); filter by status to narrow.
    const table = useServerTable((query) => gate.guard(appId, groupsService.getPage(appId, query)), {
        deps: [appId],
        enabled: Boolean(appId),
        defaultSort: { key: 'name', dir: 'asc' },
        columns,
    });

    const membersTable = useServerTable((query) => groupsService.getMembersPage(membersGroup.group_id, appId, query), {
        deps: [membersGroup?.group_id, appId],
        enabled: Boolean(membersGroup),
        defaultSort: { key: 'name', dir: 'asc' },
        columns: memberColumns,
        urlState: false, // lives in a modal: keep the page URL for the groups table
    });

    // Groups, members and pickers all belong to the selected app: close anything open on switch.
    useEffect(() => {
        setMembersGroup(null);
        setShowCreateModal(false);
        setShowAddMemberModal(false);
        setSelectedGroup(null);
        setParentOptions([]);
        setError('');
    }, [appId]);

    const openCreateModal = async () => {
        setError('');
        setShowCreateModal(true);
        // Parent picker needs every active group, not just the table's current page.
        try {
            const data = await groupsService.getAll(appId);
            setParentOptions(data || []);
        } catch (err) {
            // Feature off is shown by the page's empty state; other failures leave the picker empty.
            if (!isFeatureOffError(err)) setError(errorText(err, 'Couldn’t load parent groups.'));
            setParentOptions([]);
        }
    };

    const handleCreateGroup = async () => {
        try {
            await groupsService.create({ ...formData, app_id: appId });
            notice.success(`Group ${formData.group_name} created.`);
            setShowCreateModal(false);
            setFormData({ group_name: '', group_type: 'team', description: '', parent_group_id: '' });
            table.reload();
        } catch (err) {
            setError(errorText(err, 'Failed to create group.'));
        }
    };

    const handleToggleStatus = async (group) => {
        try {
            await groupsService.updateStatus(group.group_id, group.is_active ? 0 : 1);
            notice.success(`Group ${group.group_name} ${group.is_active ? 'deactivated' : 'activated'}.`);
            table.reload();
        } catch (err) {
            notice.error(
                err,
                `Couldn’t ${group.is_active ? 'deactivate' : 'activate'} group ${group.group_name}. Try again.`
            );
        }
    };

    const handleAddMember = async () => {
        try {
            await groupsService.addMember({
                group_id: selectedGroup.group_id,
                user_id: memberFormData.user_id,
                role_in_group: memberFormData.role_in_group,
            });
            notice.success(`Member added to ${selectedGroup.group_name}.`);
            setShowAddMemberModal(false);
            setMemberFormData({ user_id: '', role_in_group: 'member' });
            if (membersGroup?.group_id === selectedGroup.group_id) membersTable.reload();
            table.reload();
        } catch (err) {
            setError(errorText(err, 'Failed to add member.'));
        }
    };

    const handleRemoveMember = async (member) => {
        const ok = await confirm({
            title: `Remove ${member.username} from ${membersGroup.group_name}?`,
            body: 'They lose any access granted through this group. Their account and other memberships are not affected.',
            confirmLabel: 'Remove',
            tone: 'danger',
        });
        if (!ok) return;
        setError('');
        try {
            await groupsService.removeMember(membersGroup.group_id, member.user_id);
            notice.success(`${member.username} removed from ${membersGroup.group_name}.`);
            membersTable.reload();
            table.reload();
        } catch (err) {
            setError(errorText(err, 'Failed to remove member.'));
        }
    };

    const openAddMember = (group) => {
        setError('');
        setSelectedGroup(group);
        setShowAddMemberModal(true);
    };

    const openMembers = (group) => {
        setError('');
        setMembersGroup(group);
    };

    const inlineError = error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</div>
    );



    if (loadingApps) return <SkeletonList />;

    return (
        <div className="space-y-6">
            <PageHeader
                title="Groups"
                actions={
                    appId &&
                    !gate.off && (
                        <Button onClick={openCreateModal}>
                            <Plus className="h-4 w-4" aria-hidden="true" /> Create Group
                        </Button>
                    )
                }
            />

            {!appId ? (
                <div className="rounded-xl border border-slate-200 bg-white p-8 text-center">
                    <p className="text-slate-600">Choose an app in the sidebar to see its groups.</p>
                </div>
            ) : gate.off ? (
                <FeatureOffState
                    feature="Groups"
                    plural
                    appName={selectedApp?.app_name}
                    description="Groups organise an app’s users into teams, departments or regions and track who belongs to each."
                />
            ) : (
                <DataTable
                    {...table.tableProps}
                    columns={columns}
                    tableKey="groups"
                    rowKey="group_id"
                    searchPlaceholder="Search name, type, description, parent…"
                    emptyMessage="No groups found. Create one to get started."
                />
            )}

            {/* Group Members Modal */}
            <Modal
                isOpen={Boolean(membersGroup)}
                onClose={() => setMembersGroup(null)}
                title={`Members of ${membersGroup?.group_name || ''}`}
                size="lg"
            >
                <div className="space-y-4">
                    {!showAddMemberModal && inlineError}
                    <DataTable
                        {...membersTable.tableProps}
                        columns={memberColumns}
                        tableKey="group-members"
                        rowKey="membership_id"
                        searchPlaceholder="Search name, email, role…"
                        emptyMessage="No members yet."
                        toolbar={
                            <button
                                type="button"
                                onClick={() => openAddMember(membersGroup)}
                                className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-indigo-600 text-sm font-medium text-white hover:bg-indigo-700"
                            >
                                <UserPlus size={14} /> Add Member
                            </button>
                        }
                    />
                </div>
            </Modal>

            {/* Create Group Modal */}
            <Modal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} title="Create Group">
                <div className="space-y-4">
                    <Field label="Group Name *">
                        <TextInput
                            value={formData.group_name}
                            onChange={(e) => setFormData({ ...formData, group_name: e.target.value })}
                            placeholder="e.g., Cardiology Team"
                        />
                    </Field>
                    <Field label="Type">
                        <Select
                            value={formData.group_type}
                            onChange={(e) => setFormData({ ...formData, group_type: e.target.value })}
                        >
                            {GROUP_TYPE_OPTIONS.map((o) => (
                                <option key={o.value} value={o.value}>
                                    {o.label}
                                </option>
                            ))}
                        </Select>
                    </Field>
                    <Field label="Description">
                        <Textarea
                            rows={2}
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        />
                    </Field>
                    <Field label="Parent Group">
                        <Select
                            value={formData.parent_group_id}
                            onChange={(e) => setFormData({ ...formData, parent_group_id: e.target.value })}
                        >
                            <option value="">None</option>
                            {parentOptions.map((g) => (
                                <option key={g.group_id} value={g.group_id}>
                                    {g.group_name}
                                </option>
                            ))}
                        </Select>
                    </Field>
                    {inlineError}
                    <div className="flex justify-end gap-2 pt-2">
                        <button
                            onClick={() => setShowCreateModal(false)}
                            className="px-4 py-2 text-sm rounded-lg border border-slate-300 hover:bg-slate-50"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleCreateGroup}
                            disabled={!formData.group_name}
                            className="px-4 py-2 text-sm rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
                        >
                            Create
                        </button>
                    </div>
                </div>
            </Modal>

            {/* Add Member Modal */}
            <Modal
                isOpen={showAddMemberModal}
                onClose={() => setShowAddMemberModal(false)}
                title={`Add Member to ${selectedGroup?.group_name || ''}`}
            >
                <div className="space-y-4">
                    <Field label="User">
                        <Select
                            value={memberFormData.user_id}
                            onChange={(e) => setMemberFormData({ ...memberFormData, user_id: e.target.value })}
                        >
                            <option value="">Select user...</option>
                            {allUsers.map((u) => (
                                <option key={u.user_id} value={u.user_id}>
                                    {u.first_name} {u.last_name} ({u.email_id})
                                </option>
                            ))}
                        </Select>
                    </Field>
                    <Field label="Role in Group">
                        <Select
                            value={memberFormData.role_in_group}
                            onChange={(e) => setMemberFormData({ ...memberFormData, role_in_group: e.target.value })}
                        >
                            {MEMBER_ROLE_OPTIONS.map((o) => (
                                <option key={o.value} value={o.value}>
                                    {o.label}
                                </option>
                            ))}
                        </Select>
                    </Field>
                    {usersQuery.error && <ErrorState message={usersQuery.error} />}
                    {inlineError}
                    <div className="flex justify-end gap-2 pt-2">
                        <button
                            onClick={() => setShowAddMemberModal(false)}
                            className="px-4 py-2 text-sm rounded-lg border border-slate-300 hover:bg-slate-50"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleAddMember}
                            disabled={!memberFormData.user_id}
                            className="px-4 py-2 text-sm rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
                        >
                            Add Member
                        </button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default GroupsPage;
